import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Checkbox,
  Spin,
  Empty,
  Typography,
  Space,
  Button,
  Alert,
  Tag,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ReviEvaluationsApi } from "../../../api/revi/ReviEvaluationsApi";
import { PatientReviApi } from "../../../api/revi/patientApi";
import { buildAnswerItems } from "../EvaluationAnswersForm/buildAnswerItems";
import "./QuickEvaluationModal.scss";
import { useHistorialHoja } from "../../../hooks/useHistorialHoja";
import { useMovil } from "../../../hooks/useIsNarrow";
import { MAX_ITEMS_MOVIL } from "../../../ui/limitesMovil";

const { Title, Text, Paragraph } = Typography;

const evalApi = new ReviEvaluationsApi();
const patientApi = new PatientReviApi();

function hasRealQuestions(ev) {
  if (!ev || !Array.isArray(ev.questions)) return false;
  return ev.questions.some(
    (q) => typeof q?.text === "string" && q.text.trim().length > 0,
  );
}

// El respaldo cuando el paciente no tiene nombre ni correo SI es interfaz,
// asi que entra por parametro en vez de estar clavado aqui.
function patientFullName(p, sinNombre) {
  const first = p?.name || p?.firstName || "";
  const last = p?.surname || p?.lastName || "";
  return `${first} ${last}`.trim() || p?.email || sinNombre;
}

export function QuickEvaluationModal({
  isOpen,
  onClose,
  evaluation,
  accessToken,
  messageApi,
}) {
  // Solo rotulos y avisos. El titulo de la evaluacion, sus enunciados y las
  // respuestas del paciente son contenido clinico: no se tocan.
  const { t } = useTranslation(["evaluations", "common"]);
  const sinNombre = t("common:roles.patient");

  const movil = useMovil();

  // El boton atras del telefono cierra la hoja en vez de sacar de la pagina.
  // Solo en movil: en escritorio ese boton tiene que seguir navegando.
  useHistorialHoja(movil && isOpen, onClose);

  // La puerta se acepta una vez por apertura de la hoja.
  const [puertaAceptada, setPuertaAceptada] = useState(false);

  const [form] = Form.useForm();

  const [fullTemplate, setFullTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const [patients, setPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const [saving, setSaving] = useState(false);

  const notify = useMemo(
    () =>
      messageApi || {
        success: () => {},
        error: () => {},
        warning: () => {},
      },
    [messageApi],
  );

  // Reset cuando se cierra
  useEffect(() => {
    if (isOpen) return;
    setFullTemplate(null);
    setPatients([]);
    setSelectedIds([]);
    setSearch("");
    setSaving(false);
    setPuertaAceptada(false);
    // El <Form> solo se monta cuando hay preguntas cargadas, asi que al cerrar
    // el modal la instancia puede no estar conectada a ningun elemento. Sin la
    // guarda, antd avisa por consola de un `useForm` sin Form en cada cierre, y
    // ademas no habria nada que limpiar.
    if (fullTemplate) form.resetFields();
  }, [isOpen, form, fullTemplate]);

  // Hidratar plantilla completa si viene light
  useEffect(() => {
    if (!isOpen || !evaluation?._id || !accessToken) return;

    if (hasRealQuestions(evaluation)) {
      setFullTemplate(evaluation);
      return;
    }

    let cancelled = false;
    setLoadingTemplate(true);
    evalApi
      .getEvaluationById(accessToken, evaluation._id)
      .then((full) => {
        if (!cancelled) setFullTemplate(full || evaluation);
      })
      .catch((e) => {
        if (!cancelled) {
          notify.error?.(e?.message || t("aplicarTest.errorCargarTest"));
          setFullTemplate(evaluation);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplate(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, evaluation, accessToken, notify, t]);

  // Cargar pacientes del terapeuta
  useEffect(() => {
    if (!isOpen || !accessToken) return;

    let cancelled = false;
    setLoadingPatients(true);
    patientApi
      .getMyForTherapist(accessToken, { page: 1, limit: 100 })
      .then((res) => {
        if (!cancelled) setPatients(res?.patients || []);
      })
      .catch((e) => {
        if (!cancelled) {
          notify.error?.(e?.message || t("aplicarTest.errorCargarPacientes"));
          setPatients([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPatients(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, accessToken, notify, t]);

  const togglePatient = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const filteredPatients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter((p) => {
      const name = patientFullName(p, sinNombre).toLowerCase();
      const dni = (p?.dni || "").toLowerCase();
      return name.includes(q) || dni.includes(q);
    });
  }, [patients, search, sinNombre]);

  const doSubmit = useCallback(
    async (answersObj) => {
      const answers = Object.entries(answersObj?.q || {}).map(
        ([questionId, value]) => ({ questionId, value }),
      );

      setSaving(true);
      try {
        await Promise.all(
          selectedIds.map((patientId) =>
            evalApi.submitResult(accessToken, evaluation._id, {
              patientId,
              answers,
              comments: "",
            }),
          ),
        );
        notify.success?.(
          t("aplicarTest.guardada", { count: selectedIds.length }),
        );
        onClose?.();
      } catch (e) {
        notify.error?.(e?.message || t("aplicarTest.errorGuardar"));
      } finally {
        setSaving(false);
      }
    },
    [selectedIds, accessToken, evaluation, notify, onClose, t],
  );

  const handleOk = useCallback(async () => {
    if (selectedIds.length === 0) {
      notify.warning?.(t("aplicarTest.avisoSinPaciente"));
      return;
    }

    let values;
    try {
      values = await form.validateFields();
    } catch {
      notify.warning?.(t("aplicarTest.avisoRevisaRespuestas"));
      return;
    }

    if (selectedIds.length > 1) {
      Modal.confirm({
        title: t("aplicarTest.confirmarTitulo"),
        content: t("aplicarTest.confirmarCuerpo", {
          count: selectedIds.length,
        }),
        okText: t("common:actions.save"),
        cancelText: t("common:actions.cancel"),
        onOk: () => doSubmit(values),
      });
      return;
    }

    await doSubmit(values);
  }, [selectedIds, form, notify, doSubmit, t]);

  // El titulo lo escribio el terapeuta; el respaldo es interfaz.
  const title = evaluation?.title || t("aplicarTest.titulo");
  const answerItems = useMemo(
    () => (fullTemplate ? buildAnswerItems(fullTemplate, t) : null),
    [fullTemplate, t],
  );

  return (
    <Modal
      className="revi-quick-eval-modal"
      rootClassName="revi-hoja"
      title={
        <Space size={8}>
          <span>{t("aplicarTest.tituloConNombre")}</span>
          <Text strong>{title}</Text>
        </Space>
      }
      open={isOpen}
      onCancel={onClose}
      onOk={handleOk}
      okText={t("aplicarTest.guardarPara", { count: selectedIds.length })}
      cancelText={t("common:actions.cancel")}
      okButtonProps={{ loading: saving, disabled: loadingTemplate }}
      cancelButtonProps={{ disabled: saving }}
      width={820}
      destroyOnClose
      maskClosable={!saving}
    >
      <Space
        orientation="vertical"
        size={16}
        style={{ width: "100%", display: "flex" }}
      >
        {/* Bloque 1: selector de pacientes con toggles */}
        <div className="revi-quick-eval-modal__section">
          <Title level={5} style={{ margin: 0 }}>
            <UserOutlined /> {t("aplicarTest.pacientes")}
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("aplicarTest.ayudaPacientes")}
          </Text>

          <Input.Search
            allowClear
            placeholder={t("aplicarTest.buscarPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginTop: 8, marginBottom: 8 }}
          />

          {loadingPatients ? (
            <div style={{ textAlign: "center", padding: 16 }}>
              <Spin />
            </div>
          ) : filteredPatients.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("aplicarTest.sinPacientes")}
            />
          ) : (
            <div className="revi-quick-eval-modal__patients-list">
              {filteredPatients.map((p) => {
                const id = p._id;
                const checked = selectedIds.includes(id);
                return (
                  <label
                    key={id}
                    className={`revi-quick-eval-modal__patient-row${checked ? " is-selected" : ""}`}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={() => togglePatient(id)}
                    />
                    <span className="revi-quick-eval-modal__patient-name">
                      {patientFullName(p, sinNombre)}
                    </span>
                    {p?.dni ? (
                      <Tag className="revi-quick-eval-modal__patient-dni">
                        {p.dni}
                      </Tag>
                    ) : null}
                  </label>
                );
              })}
            </div>
          )}

          {selectedIds.length > 0 ? (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 8 }}
              message={
                selectedIds.length === 1
                  ? "1 paciente seleccionado"
                  : `${selectedIds.length} pacientes seleccionados`
              }
            />
          ) : null}
        </div>

        {/* Bloque 2: formulario del test */}
        <div className="revi-quick-eval-modal__section">
          <Title level={5} style={{ margin: 0 }}>
            {t("aplicarTest.respuestas")}
          </Title>
          {evaluation?.description ? (
            <Paragraph
              type="secondary"
              style={{ marginTop: 4, marginBottom: 8 }}
              ellipsis={{ rows: 2 }}
            >
              {evaluation.description}
            </Paragraph>
          ) : null}

          {loadingTemplate ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <Spin />
            </div>
          ) : !fullTemplate ||
            !Array.isArray(fullTemplate.questions) ||
            fullTemplate.questions.length === 0 ? (
            <Empty description={t("aplicarTest.sinPreguntas")} />
          ) : movil &&
            !puertaAceptada &&
            fullTemplate.questions.length > MAX_ITEMS_MOVIL ? (
            /*
              Puerta honesta antes del item 1. Una escala de 39 items son unos
              diez minutos de trabajo seguido: decirlo aqui cuesta un toque, y
              descubrirlo a mitad de camino cuesta la sesion.
            */
            <div className="revi-quick-eval-modal__puerta">
              <Text strong>
                {t("aplicarTest.puerta.titulo", {
                  count: fullTemplate.questions.length,
                })}
              </Text>
              <Paragraph type="secondary" style={{ marginTop: 8 }}>
                {t("aplicarTest.puerta.cuerpo")}
              </Paragraph>
              <Button
                type="primary"
                block
                onClick={() => setPuertaAceptada(true)}
              >
                {t("aplicarTest.puerta.continuar")}
              </Button>
            </div>
          ) : (
            <Form
              form={form}
              layout="vertical"
              className="revi-quick-eval-modal__form"
            >
              {/* Cuantas van y cuantas faltan: sin esto, en una escala larga no
                  hay forma de saber por donde se va. */}
              {movil ? (
                <div className="revi-quick-eval-modal__contador">
                  {t("aplicarTest.contador", {
                    total: fullTemplate.questions.length,
                  })}
                </div>
              ) : null}
              {answerItems}
            </Form>
          )}
        </div>
      </Space>
    </Modal>
  );
}
